import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Megaphone, TrendingUp, Globe, Target } from "lucide-react";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fINR = (v) => { if (!v) return "0"; if (v >= 10000000) return `${(v/10000000).toFixed(1)}Cr`; if (v >= 100000) return `${(v/100000).toFixed(1)}L`; return `${(v/1000).toFixed(0)}K`; };

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: {typeof p.value === 'number' && p.value > 1000 ? fINR(p.value) : p.value?.toFixed?.(1) || p.value}</p>)}
    </div>
  );
};

export default function AdsTraffic() {
  const [adsData, setAdsData] = useState(null);
  const [cacData, setCacData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/financial/ads-traffic`),
      axios.get(`${API}/cohort/cac-analysis`),
    ]).then(([aRes, cRes]) => {
      setAdsData(aRes.data);
      setCacData(cRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !adsData) return <div className="h-64 skeleton-shimmer rounded" />;

  const ads = adsData.ads || [];
  const traffic = adsData.traffic || [];
  const summary = adsData.summary || {};
  const cacCohort = cacData?.cac_by_cohort || [];
  const avgLtvCac = cacData?.avg_ltv_cac_ratio || 0;
  const repLtv = cacData?.repeat_ltv_12m || 0;
  const allLtv = cacData?.all_ltv_12m || 0;

  const trafficChart = traffic.map((t) => ({
    month: t.month, website: t.website_total, app: t.app_total,
    web_organic: t.website_organic, app_organic: t.app_organic,
  }));

  return (
    <div className="space-y-5" data-testid="ads-traffic-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Ads, Traffic & CAC Analysis</h1>
        <p className="text-sm text-slate-400 mt-1">Marketing efficiency, customer acquisition cost &amp; LTV/CAC analysis &middot; {summary.months_covered} months</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Total Ad Spend", value: `INR ${fINR(summary.total_spend)}`, color: "text-rose-400" },
          { label: "Avg ROAS", value: `${summary.avg_roas}x`, color: "text-blue-400" },
          { label: "Avg CAC", value: `INR ${summary.avg_cac}`, color: "text-amber-400" },
          { label: "Repeat LTV (12M)", value: `INR ${fINR(repLtv)}`, color: "text-emerald-400" },
          { label: "Avg LTV/CAC Ratio", value: `${avgLtvCac}x`, color: "text-violet-400" },
          { label: "All-Cust LTV (12M)", value: `INR ${fINR(allLtv)}`, color: "text-slate-300" },
        ].map((k) => (
          <div key={k.label} className="glass-card rounded-md p-3">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</p>
            <p className={`font-mono text-base mt-0.5 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* LTV/CAC Ratio Chart - THE HERO CHART */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="ltv-cac-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-400" /> LTV/CAC Ratio by Cohort Month
            <Badge variant="outline" className="text-emerald-400 border-emerald-800 bg-emerald-950/30 text-[10px] ml-2">
              Avg: {avgLtvCac}x
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cacCohort}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} axisLine={false} interval={1} angle={-40} textAnchor="end" height={45} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}x`} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                    <p className="font-semibold text-slate-200 mb-1">{d.month}</p>
                    <p className="text-slate-400">New Customers: <span className="font-mono text-slate-200">{d.new_customers?.toLocaleString()}</span></p>
                    <p className="text-slate-400">Ad Spend: <span className="font-mono text-rose-400">INR {fINR(d.ad_spend)}</span></p>
                    <p className="text-slate-400">CAC: <span className="font-mono text-amber-400">INR {d.cac}</span></p>
                    <p className="text-slate-400">LTV/CAC: <span className="font-mono text-emerald-400">{d.ltv_cac_ratio}x</span></p>
                  </div>
                );
              }} />
              <Bar dataKey="ltv_cac_ratio" name="LTV/CAC" radius={[4, 4, 0, 0]}>
                {cacCohort.map((c, i) => (
                  <Cell key={i} fill={c.ltv_cac_ratio >= 12 ? "#10b981" : c.ltv_cac_ratio >= 8 ? "#3b82f6" : "#f59e0b"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <AnnotationBanner title="LTV/CAC Analysis — Key Investor Metric">
        <p>LTV/CAC ratio is improving from ~8x (2023) to ~15x (2025) — indicating the brand is acquiring customers more efficiently as organic pull grows. Industry benchmark for healthy D2C is 3x+; StarAndDaisy is at <strong className="text-amber-300">{avgLtvCac}x average</strong>, well above threshold. This uses Repeat Customer LTV (INR {fINR(repLtv)}) as the LTV benchmark — the conservative view since it only captures website-trackable repurchases. Actual cross-platform LTV is higher. The improving trend reflects growing brand awareness (less spend needed per acquisition) and higher-quality customers entering the funnel.</p>
      </AnnotationBanner>

      {/* CAC Trend + Spend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cac-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-400" /> CAC Trend (INR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={cacCohort}>
                <defs>
                  <linearGradient id="cacGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="cac" name="CAC (INR)" stroke="#f59e0b" fill="url(#cacGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="spend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-rose-400" /> Monthly Ad Spend (Google vs Meta)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ads}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fINR} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="google_ads" name="Google" fill="#f59e0b" stackId="1" />
                <Bar dataKey="meta_ads" name="Meta" fill="#3b82f6" stackId="1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Annotation>
        CAC has been trending downward from INR 1,200+ (mid-2023) to INR 600-700 range (2025) — a ~50% improvement. This is driven by: (1) growing organic brand search, (2) reducing Meta spend without revenue impact, (3) Google dominance ({summary.google_share}% of budget) reflecting intent-driven purchase behavior. The 4M+ app install base provides a cost-free re-engagement channel further improving effective CAC.
      </Annotation>

      {/* ROAS Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="roas-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" /> ROAS Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={ads}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}x`} />
              <Tooltip content={<Tip />} />
              <Line type="monotone" dataKey="roas" name="ROAS" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2.5, fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Traffic */}
      {traffic.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="traffic-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-400" /> Website & App Traffic
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trafficChart}>
                <defs>
                  <linearGradient id="webG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                  <linearGradient id="appG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="website" name="Website" stroke="#3b82f6" fill="url(#webG)" strokeWidth={2} />
                <Area type="monotone" dataKey="app" name="App" stroke="#10b981" fill="url(#appG)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed CAC by Cohort Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cac-cohort-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200">CAC by Cohort Month</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                {["Month", "New Custs", "Ad Spend", "CAC", "ROAS", "LTV/CAC"].map((h) => (
                  <th key={h} className="text-right py-1.5 px-2 text-[10px] text-slate-500 uppercase first:text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cacCohort.map((c) => (
                <tr key={c.month} className="border-b border-slate-800/20 hover:bg-slate-800/20">
                  <td className="py-1.5 px-2 text-xs text-slate-300 font-mono">{c.month}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[11px] text-slate-300">{c.new_customers?.toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[11px] text-rose-400">INR {fINR(c.ad_spend)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[11px] text-amber-400">INR {c.cac}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[11px] text-blue-400">{c.roas}x</td>
                  <td className="py-1.5 px-2 text-right">
                    <Badge variant="outline" className={`font-mono text-[10px] ${c.ltv_cac_ratio >= 12 ? "text-emerald-400 border-emerald-800" : c.ltv_cac_ratio >= 8 ? "text-blue-400 border-blue-800" : "text-amber-400 border-amber-800"}`}>
                      {c.ltv_cac_ratio}x
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
